import React, { Component } from "react";
import { Grid } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTire } from "@fortawesome/pro-regular-svg-icons";

import { Responsive, DesktopOnly, MobileOnly } from "../../../../../layout";
import { ANSWERS } from "../../../../../util/answers";
import { getTyreItemName, getTyreSeasonIcon } from "../../../../../util/tyre";
import { QUESTION_RESULT_STATUS, round2 } from "../../../../../util/common";
import { ActionButtons, GalleryButton, Expandable, ConditionIcon } from "../../../../../components";

import "./index.scss";

const renderPrice = (price, include_vat, vat) => {
  if (!(price > 0.01)) return null;

  if (include_vat) price = price * (1 + vat / 100);

  return <span className="replacement-item-price">€ {round2(price).toFixed(2)}</span>;
};

class ReplacementItems extends Component {
  handleUpdateTyreReplacement = (itemIndex, replacementIndex, answer) => {
    const { readOnly, items, updateItems } = this.props;
    const { ADD_TO_CART, CONTACT_ME, NOT_OK, NOT_ANSWERED } = ANSWERS;

    if (readOnly) return;

    const updatedItems = [...items];

    if (updatedItems[itemIndex]?.tyre_replacements?.[replacementIndex]) {
      const item = updatedItems[itemIndex];

      const hasSelectedFixOrContact = [ADD_TO_CART, CONTACT_ME].includes(answer);
      const isAddToCartOrContactMePreviousStatus = [ADD_TO_CART, CONTACT_ME].includes(item.status);

      item.tyre_replacements.forEach((rep, idx) => {
        rep.selected = idx === replacementIndex;

        if (hasSelectedFixOrContact) {
          if (rep.selected) rep.answer = answer;
          else rep.answer = NOT_OK;
        } else {
          if (isAddToCartOrContactMePreviousStatus) {
            if (rep.selected) {
              rep.answer = answer;
            } else {
              if (answer === NOT_ANSWERED) rep.answer = NOT_ANSWERED;
            }
          } else {
            if (rep.selected) rep.answer = answer;
          }
        }
      });

      item.status = answer;
      updateItems(updatedItems);
    }
  };

  isNotFixedButtonVisible = status => {
    const { settings } = this.props;

    switch (status) {
      case QUESTION_RESULT_STATUS.NECESSARY:
        return settings.enable_not_fixed_critical;
      case QUESTION_RESULT_STATUS.ADVISED:
        return settings.enable_not_fixed_advised;
      default:
        return true;
    }
  };

  getStatus = (tyre, replacement) => {
    if (replacement.answer) return replacement.answer;
    if (replacement.selected) return tyre.status;
    if (this.props.readOnly) return ANSWERS.NOT_OK;

    return ANSWERS.NOT_ANSWERED;
  };

  renderTyreItem = (item, itemIndex) => {
    const { readOnly, settings, highlightReplacementItems, t } = this.props;

    const isHighlighted =
      highlightReplacementItems &&
      item.status === ANSWERS.NOT_ANSWERED &&
      (item.question_status === QUESTION_RESULT_STATUS.NECESSARY || (item.question_status === QUESTION_RESULT_STATUS.ADVISED && !settings.skip_optional_items));

    return (
      <Grid key={item.id} className={`replacement-item-row ${readOnly ? "read-only" : ""} ${isHighlighted ? "highlight" : ""}`}>
        <Grid.Column computer={14} widescreen={14} mobile={12} className="replacement-item-details-column">
          <div className="replacement-item-title-container">
            <ConditionIcon className="tyre-condition-icon" status={item.question_status} />
            <span>{`${getTyreItemName(t, item)} ${item.tyre?.description || ""}`}</span>
          </div>
        </Grid.Column>

        <Grid.Column computer={1} mobile={2} className="replacement-item-gallery-column">
          <div className="replacement-item-gallery">
            <GalleryButton images={item.images} videos={item.videos} />
          </div>
        </Grid.Column>

        {item.tyre_replacements?.length > 0 && (
          <Grid divided="vertically" className="replacement-options-section">
            {item.tyre_replacements.map((replacement, replacementIndex) => {
              return (
                <Grid.Row className="replacement-option-row" key={replacement.tyre_replacement_id}>
                  <Grid.Column computer={5} widescreen={6} mobile={16} className="replacement-option-details-column">
                    <div className="replacement-option-title-container">
                      <div className="replacement-option-title">
                        {getTyreSeasonIcon(replacement.tyre?.season)}
                        <span>{replacement.tyre?.manufacturer}</span>
                      </div>
                      <span className="replacement-option-description">{replacement.tyre?.description}</span>
                    </div>
                  </Grid.Column>

                  <Grid.Column computer={4} mobile={12} className="replacement-option-remark-column">
                    <div className="replacement-option-remark">
                      <Expandable text={replacement.remark} />
                    </div>
                  </Grid.Column>

                  <MobileOnly>
                    <Grid.Column mobile={4} className="replacement-option-price-mobile">
                      {renderPrice(replacement.price, settings.include_vat, settings.vat)}
                    </Grid.Column>
                  </MobileOnly>

                  <DesktopOnly>
                    <Grid.Column mobile={2} computer={1} className="replacement-option-spacer-price" />
                    <Grid.Column computer={2} className="replacement-option-price-desktop">
                      {renderPrice(replacement.price, settings.include_vat, settings.vat)}
                    </Grid.Column>
                  </DesktopOnly>

                  <Grid.Column computer={4} widescreen={3} mobile={16} className="replacement-option-actions-column">
                    <ActionButtons
                      status={this.getStatus(item, replacement)}
                      onAnswer={answer => this.handleUpdateTyreReplacement(itemIndex, replacementIndex, answer)}
                      readOnly={readOnly}
                      showNotFixedButton={this.isNotFixedButtonVisible(item.question_status)}
                    />
                  </Grid.Column>
                </Grid.Row>
              );
            })}
          </Grid>
        )}
      </Grid>
    );
  };

  render() {
    const { items, t, showMobile } = this.props;

    if (!items?.length) return null;

    return (
      <Responsive showMobile={showMobile}>
        <Grid className={`tyre-replacements-container component-container ${showMobile ? "show-mobile" : ""}`}>
          <Grid.Row className="tyre-replacements-header-row">
            <div className="tyre-replacements-header">
              <FontAwesomeIcon icon={faTire} className="tyre-replacements-header-icon" />
              <h4 className="tyre-replacements-header-title">{t("cc_tyre_replacements")?.message || "Tyre Replacements"}</h4>
            </div>
          </Grid.Row>

          <Grid.Row className="tyre-replacements-items-rows">{items.map((item, index) => this.renderTyreItem(item, index))}</Grid.Row>
        </Grid>
      </Responsive>
    );
  }
}

export default withTranslation()(ReplacementItems);
